import { Box, Collapse, Heading, Icon, Link, List, Ribbon, Text, Tooltip } from '@ksyos/design-system';
import { useState } from 'react';
import { getSelectedLanguage } from '@frontend/translate';
import type { IServiceDataItem } from '../../data/getServiceData';

interface ICardContentProps {
    data: IServiceDataItem;
}

export function ServiceCardContent({ data }: Readonly<ICardContentProps>) {
    const [showHiddenListItems, setShowHiddenListItems] = useState(false);

    const translate = getSelectedLanguage();

    const visibleListItems = data.steps.filter((step, index) => {
        if (!data.visibleStepsCount || index < data.visibleStepsCount) {
            return step;
        }
    });

    const hiddenListItems = data.steps.filter((step, index) => {
        if (data.visibleStepsCount && index >= data.visibleStepsCount) {
            return step;
        }
    });

    return (
        <Box>
            {data.isNew && (
                <Ribbon
                    position="top-right"
                    text={translate.common.new}
                />
            )}

            <Box>
                <Heading size="small">{data.title}</Heading>

                {data.subTitle && (
                    <Text
                        fontWeight="normal"
                        mb={4}
                        mt={2}
                        size="sm"
                        sx={{
                            strong: {
                                fontWeight: 'medium',
                            },
                        }}
                    >
                        {data.subTitle}
                    </Text>
                )}

                {visibleListItems.length > 0 && (
                    <>
                        <List
                            size="small"
                            styleType={data.isOrderedSteps ? 'counter' : 'bullet'}
                            sx={{ mb: 4 }}
                        >
                            {visibleListItems.map((step, index) => (
                                <List.Item key={`visibleListItems${index}`}>{step}</List.Item>
                            ))}
                            {hiddenListItems.length > 0 && (
                                <Collapse isOpen={showHiddenListItems}>
                                    {hiddenListItems.map((step, index) => (
                                        <List.Item key={`hiddenListItems${index}`}>{step}</List.Item>
                                    ))}
                                </Collapse>
                            )}
                        </List>
                        {hiddenListItems.length > 0 && (
                            <Link
                                color="subtle"
                                fontWeight="normal"
                                iconLeft={showHiddenListItems ? 'caret-up' : 'caret-down'}
                                mb={6}
                                onClick={() => setShowHiddenListItems(!showHiddenListItems)}
                                size="xs"
                                variant="button"
                            >
                                {showHiddenListItems ? 'Lees minder voorbeelden' : 'Lees meer voorbeelden'}
                            </Link>
                        )}
                    </>
                )}

                {(data.waitingTime || data.costs || data.availableDuring) && (
                    <List size="small">
                        {data.waitingTime && (
                            <List.Item
                                icon="clock"
                                iconColor="minimal"
                                sx={{ color: 'var(--kds-color-foreground-minimal)' }}
                            >
                                Doorlooptijd:&nbsp;{data.waitingTime}
                            </List.Item>
                        )}

                        {data.costs && (
                            <List.Item
                                icon="finance"
                                iconColor="minimal"
                                sx={{ color: 'var(--kds-color-foreground-minimal)' }}
                            >
                                Kosten: {data.costs}
                                {data.costsToolTipText && (
                                    <Tooltip
                                        content={data.costsToolTipText}
                                        placement="right"
                                    >
                                        <Icon
                                            color="primary"
                                            icon="information"
                                            sx={{ ml: 1, mt: '1px' }}
                                        />
                                    </Tooltip>
                                )}
                            </List.Item>
                        )}

                        {data.availableDuring && (
                            <List.Item
                                icon="calender"
                                iconColor="minimal"
                                sx={{ color: 'var(--kds-color-foreground-minimal)' }}
                            >
                                {data.availableDuring}
                            </List.Item>
                        )}
                    </List>
                )}
            </Box>
        </Box>
    );
}
